import React, { ReactElement } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  ChartDataset,
  BarElement,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { bedragFormat } from "../../../shared/utils/currency";
import defaultTheme from "../../../styles/themes/default";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export type LineChartProps = {
  labels: string[];
  datasets: ChartDataset<"line">[];
  dataObject?: ChartData<"line">;
  optionsObject?: ChartOptions<"line">;
  title?: string;
};

export type BarChartProps = {
  labels: string[];
  datasets: ChartDataset<"bar">[];
  dataObject?: ChartData<"bar">;
  optionsObject?: ChartOptions<"bar">;
  title?: string;
};

export const options: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      align: "start",
      labels: {
        boxPadding: 16,
        usePointStyle: true,
        boxHeight: 8,
        boxWidth: 8,
      },
    },
    title: {
      display: true,
      text: "Chart.js Line Chart",
    },
  },
  animation: false,
};

const labels = ["January", "February", "March", "April", "May", "June", "July"];

export const data: ChartData<"line", number[], string> = {
  labels,
  datasets: [
    {
      label: "Dataset 1",
      data: [65, 59, 80, 81, 56, 55, 40],
      borderColor: defaultTheme.colors.chartRed,
      backgroundColor: defaultTheme.colors.chartRedLight,
    },
    {
      label: "Dataset 2",
      data: [65, 59, 80, 81, 56, 55, 40],
      borderColor: defaultTheme.colors.chartBlue,
      backgroundColor: defaultTheme.colors.chartBlueLight,
    },
  ],
};

/* istanbul ignore next */
export const LineChart = ({
  title,
  labels,
  datasets,
  dataObject,
  optionsObject,
}: LineChartProps): ReactElement => {
  return (
    <div style={{ width: "595px", margin: "0 auto", paddingBottom: "20px" }}>
      <Line
        data={{
          labels,
          datasets,
          ...dataObject,
        }}
        options={{
          responsive: true,
          plugins: {
            title: {
              display: true,
              text: title,
              font: {
                size: 16,
              },
            },
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                boxHeight: 8,
                font: {
                  size: 12
                }
              },
              align: "start",
            },
            tooltip: {
              enabled: true,
            },
          },
          scales: {
            y: {
              title: {
                display: false,
              },
              min: 0,
              ticks: {
                callback: /* istanbul ignore next */
                  (value) => {
                    return bedragFormat(Number.parseFloat(value.toString()));
                  },
              }
            },
            x: {
              title: {
                display: true,
                text: "",
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          animation: {
            duration: 0,
          },
          ...optionsObject,
        }}
      />
    </div>
  );
};

/* istanbul ignore next */
export const BarChart = ({ title,
  labels,
  datasets,
  dataObject,
  optionsObject,
}: BarChartProps): ReactElement => {
  return (
    <div style={{ width: "595px", margin: "0 auto" }}>
      <Bar data={{
        labels,
        datasets,
        ...dataObject,
      }}
      options={{
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: title,
            font: {
              size: 16,
            },
          },
          legend: {
            position: "bottom",
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              boxHeight: 8,
              font: {
                size: 12
              }
            },
            align: "start",
          },
          tooltip: {
            enabled: true,
          },
        },
        scales: {
          y: {
            stacked: true,
            title: {
              display: false,
            },
            min: 0,
            ticks: {
              callback: /* istanbul ignore next */
                (value) => {
                  return bedragFormat(Number.parseFloat(value.toString()));
                },
            }
          },
          x: {
            stacked: true,
            title: {
              display: true,
              text: "",
            },
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        animation: {
          duration: 0,
        },
        ...optionsObject,
      }} />
    </div>);
};